import React, { FunctionComponent, useState } from 'react';
import CTAButton from '~/components/shared/CTAButton';
import TooltipV2 from '~/components/shared/TooltipV2';
import { useMonarchSlotContext } from '@redventures/cohesion-utils-react';
import { useVariantIds } from '~/lib/hooks/useVariantIds';
import { WALMART_URLS } from '../../../config/urls';

// Components
import MembershipCards from './components/MembershipCards';

// Data
import DEFAULT_PROPS, { primaryCTALinks } from './data';
import { MembershipTypeProps } from './data';
import classNames from 'classnames';

// Base and text classes
const rootClasses = 'pb-8';
const contentWrapperClasses = 'px-4 pt-10 text-center max-w-3xl mx-auto';
const tooltipClasses = 'fill-ozark-noir w-4';
const ctaWrapperClasses =
  'mt-10 flex justify-center items-center gap-4 flex-col md:flex-row';

const MembershipType: FunctionComponent<MembershipTypeProps> = () => {
  const { data } = useMonarchSlotContext<Omit<typeof DEFAULT_PROPS, 'idx'>>();
  const {
    variant,
    headline,
    subheadline,
    disclaimer,
    membershipCards,
    primaryCTA,
    secondaryCTA,
  } = data ?? DEFAULT_PROPS;

  const { isDarkMode } = useVariantIds();

  const [selectedCard, setSelectedCard] = useState(0);

  const handleCardSelect = (index: number) => {
    setSelectedCard(index);
  };

  const headlineClasses = classNames(
    'mb-4 font-bold text-5xl leading-[48px] tracking-[-1.44px]',
    isDarkMode ? 'text-white' : 'text-digital-walmart-blue'
  );
  const subheadlineClasses = classNames(
    'text-lg leading-[27px] mb-6',
    isDarkMode ? 'text-white' : 'text-dark-grey-3'
  );
  const secondaryCTAClassName = classNames(
    isDarkMode ? 'text-white' : 'text-black'
  );

  return (
    <div className={rootClasses}>
      <div className={contentWrapperClasses}>
        <h2 className={headlineClasses}>{headline}</h2>
        <div className={subheadlineClasses}>
          <span>{subheadline}</span>
          {disclaimer.text && (
            <TooltipV2
              text={disclaimer.text}
              link={disclaimer.link}
              elementPosition='MEMBERSHIP_TYPE'
              className={tooltipClasses}
            />
          )}
        </div>
      </div>

      <div className='my-8'>
        <MembershipCards
          cards={membershipCards}
          onSelect={handleCardSelect}
          variant={variant}
        />
      </div>

      {/* CTAs */}
      <div className={ctaWrapperClasses}>
        <CTAButton
          elementPosition='MEMBERSHIP_TYPE'
          elementName='MEMBERSHIP_TYPE_PRIMARY_CTA'
          variant={primaryCTA.variant}
          elementType='SIGN_UP_CTA_PRIMARY'
          className={selectedCard === -1 ? 'opacity-50 cursor-not-allowed' : ''}
          href={
            membershipCards[selectedCard].type === 'Annual-DM'
              ? WALMART_URLS.PLUS_ANNUAL_DM
              : primaryCTALinks[selectedCard]
          }
        >
          {primaryCTA.text}
        </CTAButton>
        {secondaryCTA && secondaryCTA.text && (
          <CTAButton
            elementPosition='SECTION'
            elementName='MEMBERSHIP_TYPE_SECONDARY_CTA'
            variant={secondaryCTA.variant}
            elementType='SIGN_UP_CTA_SECONDARY'
            href={secondaryCTA.link}
            className={secondaryCTAClassName}
          >
            {secondaryCTA.text}
          </CTAButton>
        )}
      </div>
      {/* end CTAs */}
    </div>
  );
};

export default MembershipType;
